import React from 'react';
import { motion } from 'framer-motion';
import { 
  Play, Scissors, Zap, Mic, Upload, Download, 
  Film, Music, Sparkles, ArrowRight, CheckCircle,
  Volume2, Palette, Type, Layers
} from 'lucide-react';

interface HomePageProps {
  onStartEditing: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ onStartEditing }) => {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-green-50 to-green-100">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="md:flex md:items-center md:justify-between"
          >
            <div className="text-center md:text-left md:w-1/2">
              <div className="inline-flex items-center mb-6 bg-green-100 px-4 py-2 rounded-full">
                <Zap className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-green-800">Powered by AI</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                Revolutionize Your Media Editing with AI
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-xl mx-auto md:mx-0">
                Edit videos and audio using simple natural language commands. No expertise required.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onStartEditing}
                className="bg-green-500 text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-green-600 transition-colors shadow-lg flex items-center space-x-2 mx-auto md:mx-0"
              >
                <Sparkles className="w-5 h-5" />
                <span>Get Started Free</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
            <div className="hidden md:block md:w-1/2 pl-12">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1 }}
                className="bg-green-200 rounded-2xl p-8"
              >
                <div className="bg-white rounded-xl p-4 mb-4 shadow-md">
                  <p className="text-sm text-gray-500 italic">"Cut the first 10 seconds and add cinematic filter"</p>
                </div>
                <div className="flex justify-between">
                  <Film className="w-12 h-12 text-green-500" />
                  <Music className="w-12 h-12 text-green-500" />
                  <Mic className="w-12 h-12 text-green-500" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/4 w-64 h-64 bg-green-100 rounded-full opacity-30 blur-3xl"
          />
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-green-200 rounded-full opacity-30 blur-3xl"
          />
        </div>
      </section>

      {/* What is this? Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-green-600">What is AI Media Editor?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              A cutting-edge platform that lets you edit media using everyday language. Say goodbye to complex tools.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Film,
                title: "Video Editing",
                description: "Trim, merge, filters, transitions, color grading, text, and pro exports."
              },
              {
                icon: Music,
                title: "Audio Processing",
                description: "Cut, pitch shift, noise removal, effects, and multi-format exports."
              },
              {
                icon: Mic,
                title: "AI Commands",
                description: "Edit with voice or text commands. Simple and intuitive."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-green-50 rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow"
              >
                <item.icon className="w-12 h-12 text-green-500 mb-4 mx-auto" />
                <h3 className="text-2xl font-bold mb-4 text-center">{item.title}</h3>
                <p className="text-gray-600 text-center">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="py-20 bg-green-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-green-600">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple steps to professional edits
            </p>
          </motion.div>

          <div className="relative">
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-green-200 -translate-x-1/2"></div>
            {[
              {
                step: "1",
                icon: Upload,
                title: "Upload Your Media",
                description: "Support for all major video and audio formats."
              },
              {
                step: "2", 
                icon: Mic,
                title: "Command the AI",
                description: "Use natural language to describe your edits."
              },
              {
                step: "3",
                icon: Play,
                title: "Preview Instantly",
                description: "See changes in real-time with comparisons."
              },
              {
                step: "4",
                icon: Download,
                title: "Export & Share",
                description: "Download in high quality formats."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`md:flex items-center mb-12 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}
              >
                <div className={`bg-white rounded-2xl p-6 shadow-md md:w-2/5 ${index % 2 === 0 ? 'md:ml-12' : 'md:mr-12'}`}>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
                <div className="hidden md:block md:w-1/5 text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto text-white font-bold text-xl">
                    {item.step}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-green-600">Powerful Features</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              AI-driven tools for professional results
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Video Features */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-green-50 rounded-2xl p-8 shadow-md"
            >
              <div className="flex items-center mb-6">
                <Film className="w-8 h-8 text-green-500 mr-3" />
                <h3 className="text-2xl font-bold">Video Tools</h3>
              </div>
              <ul className="space-y-4">
                {[
                  { icon: Scissors, text: "Precision Cutting & Trimming" },
                  { icon: Layers, text: "Seamless Merging" },
                  { icon: Palette, text: "Advanced Filters" },
                  { icon: Sparkles, text: "Auto Color Correction" },
                  { icon: Type, text: "Dynamic Text Overlays" },
                  { icon: Play, text: "Professional Transitions" }
                ].map((feature, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <feature.icon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Audio Features */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-green-50 rounded-2xl p-8 shadow-md"
            >
              <div className="flex items-center mb-6">
                <Music className="w-8 h-8 text-green-500 mr-3" />
                <h3 className="text-2xl font-bold">Audio Tools</h3>
              </div>
              <ul className="space-y-4">
                {[
                  { icon: Scissors, text: "Audio Trimming" },
                  { icon: Layers, text: "Track Merging" },
                  { icon: Volume2, text: "Volume Normalization" },
                  { icon: Zap, text: "Pitch & Speed Control" },
                  { icon: Sparkles, text: "Noise Cancellation" },
                  { icon: Mic, text: "Voice Enhancement" }
                ].map((feature, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <feature.icon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* AI Commands Examples */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-12 bg-green-50 rounded-2xl p-8 shadow-md"
          >
            <div className="flex items-center mb-6">
              <Mic className="w-8 h-8 text-green-500 mr-3" />
              <h3 className="text-2xl font-bold">Command Examples</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Cut the first 10 seconds",
                "Make it black and white", 
                "Apply cinematic filter",
                "Merge clip1.mp4 and clip2.mp4",
                "Change pitch to robotic",
                "Remove background noise",
                "Speed up by 2x",
                "Add fade transition",
                "Make voice deeper"
              ].map((command, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                  <span className="text-gray-600 italic">"{command}"</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-green-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-green-600">
              Start Creating Today
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join creators worldwide in transforming ideas into stunning media
            </p>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onStartEditing}
              className="bg-green-500 text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-green-600 transition-colors shadow-lg flex items-center space-x-2 mx-auto"
            >
              <Sparkles className="w-5 h-5" />
              <span>Begin Your Journey</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;